import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { 
  Flame, TrendingUp, Wallet, Clock, Gift, Trophy, 
  ChevronRight, Star, ArrowUpRight, Zap, Calendar,
  Target, Award
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';

export default function BARKAWalletPage() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [alreadyCheckedIn, setAlreadyCheckedIn] = useState(false);

  useEffect(() => {
    fetchWalletData();
  }, []);

  const fetchWalletData = async () => {
    try {
      const [walletRes, tiersRes] = await Promise.all([
        api.get('/wallet'),
        api.get('/wallet/tiers')
      ]);
      
      setWallet(walletRes.data.wallet);
      setStats(walletRes.data.stats);
      setTransactions(walletRes.data.recent_transactions || []);
      setTiers(tiersRes.data.tiers || []);
      
      // Check if already checked in today
      const today = new Date().toISOString().split('T')[0];
      const lastCheckIn = walletRes.data.wallet?.last_check_in_date;
      setAlreadyCheckedIn(lastCheckIn === today);
    } catch (error) {
      console.error('Failed to fetch wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDailyCheckIn = async () => {
    if (alreadyCheckedIn) {
      alert('Vous avez déjà réclamé votre bonus aujourd\'hui ! Revenez demain 🌅');
      return;
    }
    
    setCheckingIn(true);
    try {
      const res = await api.post('/wallet/check-in');
      
      if (res.data.already_claimed) {
        // Already claimed today - update state and show info
        setAlreadyCheckedIn(true);
        alert('Vous avez déjà réclamé votre bonus aujourd\'hui ! Revenez demain 🌅');
      } else if (res.data.success) {
        // Success - mark as claimed, refresh and show message
        setAlreadyCheckedIn(true);
        await fetchWalletData();
        let message = `+${res.data.daily_barka} BARKA! Streak: ${res.data.streak} jours 🔥`;
        if (res.data.streak_bonus_unlocked) {
          message += '\n🎉 Bonus de streak débloqué!';
        }
        alert(message);
      }
    } catch (error) {
      console.error('Check-in failed:', error);
      alert('Erreur lors du check-in. Réessayez plus tard.');
    } finally {
      setCheckingIn(false);
    }
  };

  const getTierIcon = (tier) => {
    switch(tier) {
      case 'bronze': return '🥉';
      case 'silver': return '🥈';
      case 'gold': return '🥇';
      case 'diamond': return '💎';
      default: return '🏅';
    }
  };

  const getTierColor = (tier) => {
    switch(tier) {
      case 'bronze': return 'from-amber-600 to-amber-800';
      case 'silver': return 'from-gray-300 to-gray-500';
      case 'gold': return 'from-yellow-400 to-yellow-600';
      case 'diamond': return 'from-cyan-300 to-blue-500';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  const getTransactionIcon = (type) => {
    if (type.includes('like')) return '❤️';
    if (type.includes('comment')) return '💬';
    if (type.includes('follower')) return '👤';
    if (type.includes('post') || type.includes('recipe')) return '📝';
    if (type.includes('challenge')) return '🏆';
    if (type.includes('login') || type.includes('streak')) return '🔥';
    if (type.includes('welcome')) return '🎁';
    return '✨';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a1628] to-[#1a2744] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#E07A5F]"></div>
      </div>
    );
  }

  const progressToNextTier = stats?.barka_to_next_tier > 0 
    ? ((wallet?.lifetime_barka || 0) / (wallet?.lifetime_barka + stats.barka_to_next_tier)) * 100
    : 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] to-[#1a2744] text-white pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E4ED8] to-[#3B82F6] px-4 pt-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Flame className="text-orange-400" />
              Mon Wallet BARKA
            </h1>
            <Button 
              onClick={handleDailyCheckIn}
              disabled={checkingIn || alreadyCheckedIn}
              className={alreadyCheckedIn 
                ? "bg-gray-500 cursor-not-allowed text-white" 
                : "bg-orange-500 hover:bg-orange-600 text-white"}
              data-testid="daily-checkin-btn"
            >
              {checkingIn ? 'Chargement...' : alreadyCheckedIn ? '✓ Réclamé' : '🔥 Check-in +5'}
            </Button>
          </div>
          
          {/* Main Balance Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm mb-1">Solde disponible</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-orange-400" data-testid="barka-balance">
                    {wallet?.available_barka?.toLocaleString() || 0}
                  </span>
                  <span className="text-xl text-white/60">BARKA</span>
                </div>
                <p className="text-white/50 text-sm mt-1">
                  ≈ {stats?.estimated_tnd_value?.toFixed(2) || '0.00'} TND
                </p>
              </div>
              <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${getTierColor(wallet?.tier)} flex items-center justify-center text-3xl`}>
                {getTierIcon(wallet?.tier)}
              </div>
            </div>
            
            {/* Pending BARKA */}
            {wallet?.pending_barka > 0 && (
              <div className="mt-4 p-3 bg-yellow-500/20 rounded-lg flex items-center gap-2">
                <Clock className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-200 text-sm">
                  {wallet.pending_barka.toLocaleString()} BARKA en attente de validation
                </span>
              </div>
            )}
          </motion.div>
        </div>
      </div>
      
      {/* Stats Grid */}
      <div className="max-w-4xl mx-auto px-4 -mt-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#1a2744]/80 backdrop-blur rounded-xl p-4 border border-white/10"
          >
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-blue-400" />
              <span className="text-white/60 text-xs">Ce mois</span>
            </div>
            <p className="text-xl font-bold text-blue-400">
              +{stats?.barka_earned_this_month?.toLocaleString() || 0}
            </p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#1a2744]/80 backdrop-blur rounded-xl p-4 border border-white/10"
          >
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-orange-400" />
              <span className="text-white/60 text-xs">Streak</span>
            </div>
            <p className="text-xl font-bold text-orange-400">
              {stats?.current_streak || 0} jours
            </p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-[#1a2744]/80 backdrop-blur rounded-xl p-4 border border-white/10"
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-green-400" />
              <span className="text-white/60 text-xs">Taux</span>
            </div>
            <p className="text-xl font-bold text-green-400">
              {((stats?.current_conversion_rate || 0.001) * 1000).toFixed(2)} TND
            </p>
            <p className="text-xs text-white/40">/ 1000 BARKA</p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-[#1a2744]/80 backdrop-blur rounded-xl p-4 border border-white/10"
          >
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-4 h-4 text-emerald-400" />
              <span className="text-white/60 text-xs">TND</span>
            </div>
            <p className="text-xl font-bold text-emerald-400">
              {wallet?.tnd_balance?.toFixed(2) || '0.00'}
            </p>
          </motion.div>
        </div>
        
        {/* Tier Progress */}
        <Card className="bg-[#1a2744]/80 border-white/10 mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-400" />
              Progression Tier
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/70 flex items-center gap-1">
                {getTierIcon(wallet?.tier)} {wallet?.tier?.toUpperCase()}
              </span>
              {stats?.next_tier && (
                <span className="text-white/50 flex items-center gap-1">
                  {getTierIcon(stats.next_tier)} {stats.next_tier.toUpperCase()}
                </span>
              )}
            </div>
            <Progress value={progressToNextTier} className="h-2 mb-2" />
            {stats?.barka_to_next_tier > 0 && (
              <p className="text-white/50 text-sm">
                {stats.barka_to_next_tier.toLocaleString()} BARKA jusqu'au prochain tier
              </p>
            )}
          </CardContent>
        </Card>
        
        {/* Tier Benefits */}
        <Card className="bg-[#1a2744]/80 border-white/10 mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-400" />
              Avantages Tier {wallet?.tier?.toUpperCase()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-white/50 text-xs mb-1">Bonus conversion</p>
                <p className="text-green-400 font-bold">+{stats?.tier_benefits?.conversion_bonus || 0}%</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-white/50 text-xs mb-1">Frais retrait</p>
                <p className="text-blue-400 font-bold">{stats?.tier_benefits?.withdrawal_fee || 5}%</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-white/50 text-xs mb-1">Min. conversion</p>
                <p className="text-purple-400 font-bold">{(stats?.tier_benefits?.min_withdrawal || 10000).toLocaleString()}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-white/50 text-xs mb-1">Commission tips</p>
                <p className="text-orange-400 font-bold">{stats?.tier_benefits?.tips_commission || 30}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Recent Transactions */}
        <Card className="bg-[#1a2744]/80 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-pink-400" />
                Transactions récentes
              </span>
              <Button variant="ghost" size="sm" className="text-white/50 hover:text-white">
                Voir tout <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <p className="text-white/50 text-center py-4">Aucune transaction pour le moment</p>
            ) : (
              <div className="space-y-3">
                {transactions.map((tx, index) => (
                  <motion.div
                    key={tx.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                    data-testid={`transaction-${tx.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getTransactionIcon(tx.type)}</span>
                      <div>
                        <p className="text-white font-medium text-sm">{tx.description}</p>
                        <p className="text-white/40 text-xs">
                          {new Date(tx.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <span className={`font-bold ${tx.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {tx.amount >= 0 ? '+' : ''}{tx.amount.toLocaleString()}
                    </span>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* How to Earn BARKA */}
        <Card className="bg-gradient-to-r from-orange-500/20 to-red-500/20 border-orange-500/30 mt-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400" />
              Comment gagner des BARKA?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2 text-white/80">
                <span className="text-orange-400">📝</span> Créer un post: +50
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <span className="text-orange-400">🍳</span> Créer une recette: +100
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <span className="text-orange-400">❤️</span> Recevoir un like: +1
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <span className="text-orange-400">💬</span> Recevoir un commentaire: +2
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <span className="text-orange-400">👤</span> Nouveau follower: +10
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <span className="text-orange-400">🏆</span> Gagner un défi: +5000
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <span className="text-orange-400">🔥</span> Check-in quotidien: +5
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <span className="text-orange-400">📆</span> Streak 7 jours: +50
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Call to Action */}
        <div className="mt-6 p-6 bg-gradient-to-r from-[#1E4ED8] to-[#3B82F6] rounded-2xl text-center">
          <h3 className="text-xl font-bold mb-2">
            🌟 Transforme tes BARKA en vrai salaire!
          </h3>
          <p className="text-white/80 text-sm mb-4">
            Atteins 10,000 BARKA pour débloquer les conversions en TND
          </p>
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={() => window.location.href = '/kyc'}
              className="bg-white text-[#1E4ED8] hover:bg-white/90"
            >
              Vérifier mon identité <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
            <Button 
              onClick={() => window.location.href = '/creator'}
              variant="outline"
              className="border-white/50 text-white hover:bg-white/10"
            >
              Dashboard Créateur
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
